/**
 * Customer-friendly Stripe decline messages.
 *
 * Stripe surfaces the real reason for every card failure via two fields:
 *   - `code`         (e.g. "card_declined", "expired_card", "incorrect_cvc")
 *   - `decline_code` (e.g. "insufficient_funds", "lost_card", "do_not_honor")
 *
 * Stripe.js puts them on the `error` object returned from `stripe.confirmCardPayment` /
 * `stripe.confirmCardSetup` / `stripe.handleCardAction`. Our backend forwards the same
 * fields on JSON error responses (see `server/lib/stripeErrors.js` and
 * `supabase/functions/_shared/stripeErrorMessage.ts`), and on off-session
 * PaymentIntents the same fields live under `last_payment_error`.
 *
 * `formatStripeErrorMessage` accepts any of those shapes and returns a single
 * customer-facing string. If we don't recognize the code, we fall back to Stripe's
 * own message — which is still better than a generic "card was declined" — and only
 * synthesize "Your card was declined ({code})" as a last resort.
 *
 * Keep the mapping conservative: showing the wrong reason ("insufficient funds" when
 * the bank actually flagged fraud) is worse than showing Stripe's raw text.
 */

const DECLINE_MESSAGES = {
  // Funds / limits
  insufficient_funds: 'Your card was declined for insufficient funds. Please try a different card.',
  card_velocity_exceeded: 'This card has hit a spending limit set by your bank. Please try another card or contact your bank.',
  withdrawal_count_limit_exceeded: 'This card has reached its transaction limit for now. Please try another card or try again later.',

  // Card status
  expired_card: 'Your card has expired. Please use a different card.',
  lost_card: 'This card was reported lost and cannot be used. Please use a different card.',
  stolen_card: 'This card was reported stolen and cannot be used. Please use a different card.',
  pickup_card: 'Your bank has requested that this card not be used. Please use a different card.',
  restricted_card: 'This card cannot be used for this purchase. Please use a different card.',
  revocation_of_authorization: 'Your bank revoked authorization for this card. Please use a different card.',
  revocation_of_all_authorizations: 'Your bank revoked authorization for this card. Please use a different card.',
  stop_payment_order: 'A stop-payment order is in place on this card. Please use a different card.',

  // Card type / capability
  card_not_supported: "Your card doesn't support this type of purchase. Please try a different card.",
  currency_not_supported: "Your card doesn't support this currency. Please try a different card.",
  service_not_allowed: "Your card doesn't support this type of purchase. Please try a different card.",
  transaction_not_allowed: "Your card doesn't allow this type of transaction. Please try a different card.",
  not_permitted: 'This transaction is not permitted on your card. Please use a different card.',
  /**
   * Synthetic decline_code emitted by our edge functions when we detect
   * `payment_method.card.funding === 'prepaid'` before charging. Stripe itself
   * does not return this string — the Radar rule that blocks prepaid cards in
   * production returns `generic_decline`, which is indistinguishable from a
   * genuine bank decline. Pre-checking funding lets us surface the real reason.
   */
  prepaid_card_not_supported: "Prepaid cards aren't supported for installment plans. Please use a credit or debit card.",

  // Card details
  incorrect_cvc: 'The security code (CVC) is incorrect. Please re-enter your card details.',
  invalid_cvc: 'The security code (CVC) is invalid. Please re-enter your card details.',
  incorrect_number: 'The card number is incorrect. Please re-enter your card details.',
  invalid_number: 'The card number is invalid. Please re-enter your card details.',
  invalid_expiry_month: "The card's expiration month is invalid. Please re-enter your card details.",
  invalid_expiry_year: "The card's expiration year is invalid. Please re-enter your card details.",
  incorrect_zip: 'The ZIP / postal code is incorrect. Please re-enter your card details.',
  incorrect_address: 'The billing address is incorrect. Please re-enter your card details.',

  // Bank / issuer responses
  do_not_honor: 'Your bank declined this card. Please contact your bank or try a different card.',
  generic_decline: 'Your bank declined this card. Please contact your bank or try a different card.',
  call_issuer: 'Your bank declined this card. Please contact your bank or try a different card.',
  approve_with_id: 'Your bank could not approve this charge. Please contact your bank or try a different card.',
  issuer_not_available: "We couldn't reach your bank to authorize this charge. Please try again in a moment.",
  no_action_taken: 'Your bank declined this card. Please contact your bank or try a different card.',
  new_account_information_available: 'Your card has updated information on file. Please contact your bank or use a different card.',

  // Fraud / risk
  fraudulent: 'This payment was declined due to suspected fraud. Please contact your bank or try a different card.',
  merchant_blacklist: 'This payment was declined. Please use a different card.',
  security_violation: 'This payment was declined for security reasons. Please contact your bank or try a different card.',

  // PIN / authentication
  authentication_required: 'Your bank requires additional authentication for this payment. Please complete the verification challenge.',
  online_or_offline_pin_required: 'This card requires a PIN, which is not supported online. Please use a different card.',
  pin_try_exceeded: 'You have exceeded the number of allowed PIN attempts. Please contact your bank.',

  // Temporary / processing
  processing_error: 'A temporary processing error occurred. Please try again in a moment.',
  try_again_later: 'Your bank asked us to try this charge again later. Please retry in a few minutes.',
  card_decline_rate_limit_exceeded: 'This card has been declined too many times in a row. Please wait a few minutes and try again.',
  duplicate_transaction: 'This appears to be a duplicate of a recent charge. Please check your account before retrying.',

  // Test mode (only seen in test keys; surface plainly so devs aren't confused)
  testmode_decline: 'Your card was declined (test mode). Please use a Stripe test card.',
};

/** Top-level Stripe error codes (when no `decline_code` is present). */
const CODE_MESSAGES = {
  expired_card: DECLINE_MESSAGES.expired_card,
  incorrect_cvc: DECLINE_MESSAGES.incorrect_cvc,
  invalid_cvc: DECLINE_MESSAGES.invalid_cvc,
  incorrect_number: DECLINE_MESSAGES.incorrect_number,
  invalid_number: DECLINE_MESSAGES.invalid_number,
  invalid_expiry_month: DECLINE_MESSAGES.invalid_expiry_month,
  invalid_expiry_year: DECLINE_MESSAGES.invalid_expiry_year,
  incorrect_zip: DECLINE_MESSAGES.incorrect_zip,
  processing_error: DECLINE_MESSAGES.processing_error,
  authentication_required: DECLINE_MESSAGES.authentication_required,
  card_declined: DECLINE_MESSAGES.generic_decline,
  /** Stripe.js sometimes returns this for missing required fields; show plainly. */
  incomplete_card: 'Please complete your card details and try again.',
  incomplete_cvc: 'Please enter your card security code (CVC).',
  incomplete_expiry: "Please enter your card's expiration date.",
  incomplete_number: 'Please enter a complete card number.',
  incomplete_zip: 'Please enter a ZIP / postal code.',
};

/**
 * Pull `{ code, decline_code, message }` out of any of the shapes we get from Stripe
 * or our own backend. Returns nullable strings so callers can decide on fallbacks.
 *
 * Accepted shapes:
 *   - Stripe.js error                  { code, decline_code, message, type }
 *   - Stripe Node SDK error            { type, code, decline_code, message, param, raw }
 *   - PaymentIntent.last_payment_error { code, decline_code, message }
 *   - Our backend JSON                 { error|message, code, decline_code, last_payment_error }
 *   - Plain Error                      { message }
 */
function extractStripeFields(input) {
  if (!input) return { code: null, declineCode: null, message: null };
  if (typeof input === 'string') return { code: null, declineCode: null, message: input };

  /** Backend JSON sometimes nests Stripe's last_payment_error verbatim. */
  const lpe = input.last_payment_error;
  const code = input.decline_code || input.declineCode || input.code || lpe?.decline_code || lpe?.code || null;
  const declineCode = input.decline_code || input.declineCode || lpe?.decline_code || null;

  let message = null;
  if (typeof input.error === 'string') message = input.error;
  else if (input.error?.message) message = input.error.message;
  else if (typeof input.message === 'string') message = input.message;
  else if (lpe?.message) message = lpe.message;

  return { code, declineCode, message };
}

/**
 * Translate Stripe error info into a customer-friendly line.
 *
 * @param {*} input Stripe.js error, Stripe Node error, server JSON body, last_payment_error, or string.
 * @returns {string|null} A friendly message, or null if there's nothing meaningful to show.
 */
export function formatStripeErrorMessage(input) {
  const { code, declineCode, message } = extractStripeFields(input);

  /** decline_code is more specific than code (e.g. card_declined → insufficient_funds), so prefer it. */
  if (declineCode && DECLINE_MESSAGES[declineCode]) return DECLINE_MESSAGES[declineCode];
  if (code && DECLINE_MESSAGES[code]) return DECLINE_MESSAGES[code];
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];

  if (message) return message;

  if (declineCode || code) return `Your card was declined (${declineCode || code}).`;
  return null;
}

/**
 * Convenience wrapper: same as `formatStripeErrorMessage` but always returns a non-empty
 * string by falling back to a provided default (defaults to a generic decline message).
 */
export function formatStripeErrorMessageWithFallback(input, fallback = 'Your card was declined. Please try a different card.') {
  return formatStripeErrorMessage(input) || fallback;
}
